import React from 'react';
import LandingPanel from '../../landingPagePanel/landingPagePanel';
import {ToastContainer} from "react-toastify";
import LandingSidebar from '../../landingSidebar/landingSidebar';

const heronSidebarLinks = [
    {label: 'Overview', to: '/distributed-computing/heron'},
];

// Heron is a drop-in, API-compatible successor to Apache Storm, so several
// concepts (topologies, spouts, bolts, tuples, stream groupings, acking,
// windowing...) are identical and reuse the Storm icon set rather than
// duplicating near-identical artwork. Only what's architecturally unique to
// Heron (Topology Master, Stream Manager, Heron Instance, Metrics Manager)
// gets its own icon.
const heronConcepts = [
    {id: 1, title: 'What is Apache Heron?',
        content: 'An introduction to Apache Heron, a drop-in successor to Apache Storm.',
        slug: 'what-is-apache-heron',
        image: '/images/distributed/heron-landing/what-is-apache-heron.svg',
        enabled: false,
        sourceLinks: []},
    {id: 2, title: 'Concepts in Heron',
        content: 'The concepts behind Apache Heron',
        slug: 'heron-concepts',
        image: '/images/distributed/distributed-framework-landing/concepts.svg',
        enabled: false,
        sourceLinks: []},
    {id: 3, title: 'Topologies',
        content: 'The directed graph of spouts and bolts that defines a Heron job',
        slug: 'heron-topologies',
        image: '/images/distributed/storm-landing/topologies.svg',
        enabled: false,
        sourceLinks: []},
    {id: 4, title: 'Spouts',
        content: 'The source of a stream of tuples entering a topology',
        slug: 'heron-spouts',
        image: '/images/distributed/storm-landing/spouts.svg',
        enabled: false,
        sourceLinks: []},
    {id: 5, title: 'Bolts',
        content: 'The units that process, filter, and transform tuples',
        slug: 'heron-bolts',
        image: '/images/distributed/storm-landing/bolts.svg',
        enabled: false,
        sourceLinks: []},
    {id: 6, title: 'Stream Groupings',
        content: 'How tuples are routed from one bolt to the next',
        slug: 'heron-stream-groupings',
        image: '/images/distributed/storm-landing/stream-groupings.svg',
        enabled: false,
        sourceLinks: []},
    {id: 7, title: 'Reliability and Acking',
        content: 'How Heron guarantees a tuple is fully processed',
        slug: 'heron-reliability-acking',
        image: '/images/distributed/storm-landing/reliability-acking.svg',
        enabled: false,
        sourceLinks: []},
    {id: 8, title: 'Topology Master',
        content: "Heron's per-topology coordinator, replacing Storm's shared Nimbus",
        slug: 'heron-topology-master',
        image: '/images/distributed/heron-landing/topology-master.svg',
        enabled: false,
        sourceLinks: []},
    {id: 9, title: 'Stream Manager',
        content: 'Routes tuples between Heron Instances within and across containers',
        slug: 'heron-stream-manager',
        image: '/images/distributed/heron-landing/stream-manager.svg',
        enabled: false,
        sourceLinks: []},
    {id: 10, title: 'Heron Instance',
        content: 'Every task runs in its own isolated JVM process, unlike Storm threads',
        slug: 'heron-instance',
        image: '/images/distributed/heron-landing/heron-instance.svg',
        enabled: false,
        sourceLinks: []},
    {id: 11, title: 'Metrics Manager',
        content: 'Collects and reports metrics from every container',
        slug: 'heron-metrics-manager',
        image: '/images/distributed/heron-landing/metrics-manager.svg',
        enabled: false,
        sourceLinks: []},
    {id: 12, title: 'Heron Tracker and UI',
        content: "Heron's tools for monitoring and exploring running topologies",
        slug: 'heron-tracker-ui',
        image: '/images/distributed/heron-landing/heron-tracker-ui.svg',
        enabled: false,
        sourceLinks: []}
];

const heronTopologyDetails = [
    {id: 1, title: 'Tuple Anatomy',
        content: 'The named, ordered values that make up a tuple',
        slug: 'heron-tuple-anatomy',
        image: '/images/distributed/storm-landing/tuple-anatomy.svg',
        enabled: false,
        sourceLinks: []},
    {id: 2, title: 'Bolt Lifecycle',
        content: 'Prepare, execute, and cleanup for a bolt',
        slug: 'heron-bolt-lifecycle',
        image: '/images/distributed/storm-landing/bolt-lifecycle.svg',
        enabled: false,
        sourceLinks: []},
    {id: 3, title: 'Task and Container Assignment',
        content: 'How Heron packs tasks into containers',
        slug: 'heron-task-container-assignment',
        image: '/images/distributed/heron-landing/task-container-assignment.svg',
        enabled: false,
        sourceLinks: []},
    {id: 4, title: 'Stateful and Stateless Bolts',
        content: 'Bolts that carry data between tuples, and bolts that do not',
        slug: 'heron-stateful-stateless-bolts',
        image: '/images/distributed/storm-landing/stateful-stateless-bolts.svg',
        enabled: false,
        sourceLinks: []},
    {id: 5, title: 'Windowing',
        content: 'Aggregating tuples over a sliding or tumbling window of time',
        slug: 'heron-windowing',
        image: '/images/distributed/storm-landing/windowing.svg',
        enabled: false,
        sourceLinks: []},
    {id: 6, title: 'Streamlet API',
        content: "Heron's own higher-level, functional API for building topologies",
        slug: 'heron-streamlet-api',
        image: '/images/distributed/heron-landing/streamlet-api.svg',
        enabled: false,
        sourceLinks: []},
    {id: 7, title: 'Backpressure',
        content: 'How an overloaded Heron Instance signals the Stream Manager to slow down',
        slug: 'heron-backpressure',
        image: '/images/distributed/heron-landing/backpressure.svg',
        enabled: false,
        sourceLinks: []}
];

const heronSimpleExamples = [
    {id: 1, title: 'Hello Topology',
        content: 'The simplest spout-and-bolt topology, logging every tuple it receives',
        slug: 'heron-hello-topology',
        image: '/images/distributed/heron-landing/hello-topology.svg',
        enabled: false,
        sourceLinks: []},
    {id: 2, title: 'Streamlet Word Count',
        content: 'The classic word count example built with the Streamlet API',
        slug: 'heron-streamlet-word-count',
        image: '/images/distributed/heron-landing/streamlet-word-count.svg',
        enabled: false,
        sourceLinks: []},
    {id: 3, title: 'Rolling Window Count',
        content: 'Counting events over a sliding time window',
        slug: 'heron-rolling-window-count',
        image: '/images/distributed/heron-landing/rolling-window-count.svg',
        enabled: false,
        sourceLinks: []},
    {id: 4, title: 'Backpressure Demo',
        content: 'A topology that shows how Heron slows an upstream spout when a bolt is overloaded',
        slug: 'heron-backpressure-demo',
        image: '/images/distributed/heron-landing/backpressure-demo.svg',
        enabled: false,
        sourceLinks: []}
];

const heronAdvancedExamples = [
    {id: 1, title: 'Real-Time Log Monitoring',
        content: 'Ingesting and aggregating live application logs across containers',
        slug: 'heron-log-monitoring',
        image: '/images/distributed/heron-landing/log-monitoring.svg',
        enabled: false,
        sourceLinks: []},
    {id: 2, title: 'Fraud Detection Topology',
        content: 'Joining transaction streams to flag suspicious activity in real time',
        slug: 'heron-fraud-detection-topology',
        image: '/images/distributed/heron-landing/fraud-detection-topology.svg',
        enabled: false,
        sourceLinks: []}
];

const HeronLanding = () => {
    return (
        <React.Fragment>
            <ToastContainer
                autoClose={5000}
                hideProgressBar={true}
            />
            <div className="relative space-y-4 flex flex-col md:grid md:space-y-0 w-full md:grid-cols-12 2xl:grid-cols-12 md:flex-1 md:grid-rows-full">
                <LandingSidebar title="Apache Heron" links={heronSidebarLinks}/>
                <section className="pt-2 border-gray-200 md:col-span-10 lg:col-span-10 2xl:col-span-10 pb-24">
            <div className="bg-gradient-to-br from-gray-50 via-orange-50 to-orange-50 dark:from-gray-950 dark:to-gray-900 py-16 md:py-16">
                <div className="w-full px-4 grid lg:grid-cols-3 gap-x-6 gap-y-12 lg:gap-y-0">
                    <div className="lg:pr-12 lg:place-self-top">
                        <div className="flex flex-col ">
                            <svg className="text-red-400 text-2xl" width="1.5em" height="1.5em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 13V21L5 16V8L12 3L19 8L12 13Z" fill="currentColor" opacity="0.5"/>
                                <path d="M12 21V13M12 21L5.83752 16.5982C5.42695 16.305 5.22166 16.1583 5.11083 15.943C5 15.7276 5 15.4753 5 14.9708V8M12 21L18.1625 16.5982C18.573 16.305 18.7783 16.1583 18.8892 15.943C19 15.7276 19 15.4753 19 14.9708V8M12 13L5 8M12 13L19 8M5 8L10.8375 3.83034C11.3989 3.42938 11.6795 3.2289 12 3.2289C12.3205 3.2289 12.6011 3.42938 13.1625 3.83034L19 8" stroke="#222222"  strokeWidth="1.2" opacity="0.5"/>
                            </svg>
                            <div className="h-2" />
                            <div className="flex  rounded-lg">
                                <div>
                                    <h2 className="text-2xl font-semibold
											leading-tight text-gray-800 false">Heron Concepts
                                    </h2></div>
                            </div>
                            <div className="h-6" />
                            <p className="leading-snug text-gray-500">Some of the concepts and functionality underpinning Apache Heron
                            </p>
                            <div className="h-6" />
                        </div>
                    </div>
                    <div className="lg:col-span-2 grid lg:grid-cols-2 gap-4">
                        {heronConcepts.map((data) => (
                            <LandingPanel key={data.id} title={data.title} imageSource={data.image} to={"/distributed-computing/heron/concepts/" + data.slug} strapLine={data.content} enabled={data.enabled}  />
                        ))}
                    </div>
                </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 via-orange-50 to-orange-50 dark:from-gray-950 dark:to-gray-900 py-16 md:py-16">
                <div className="w-full px-4 grid lg:grid-cols-3 gap-x-6 gap-y-12 lg:gap-y-0">
                    <div className="lg:pr-12 lg:place-self-top">
                        <div className="flex flex-col ">
                            <svg className="text-blue-400 text-2xl" width="1.5em" height="1.5em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 13V21L5 16V8L12 3L19 8L12 13Z" fill="currentColor" opacity="0.5"/>
                                <path d="M12 21V13M12 21L5.83752 16.5982C5.42695 16.305 5.22166 16.1583 5.11083 15.943C5 15.7276 5 15.4753 5 14.9708V8M12 21L18.1625 16.5982C18.573 16.305 18.7783 16.1583 18.8892 15.943C19 15.7276 19 15.4753 19 14.9708V8M12 13L5 8M12 13L19 8M5 8L10.8375 3.83034C11.3989 3.42938 11.6795 3.2289 12 3.2289C12.3205 3.2289 12.6011 3.42938 13.1625 3.83034L19 8" stroke="#222222"  strokeWidth="1.2" opacity="0.5"/>
                            </svg>
                            <div className="h-2"></div>
                            <div className="flex  rounded-lg">
                                <div>
                                    <h2 className="text-2xl font-semibold
											leading-tight text-gray-800 false">Topology Features
                                    </h2></div>
                            </div>
                            <div className="h-6"></div>
                            <p className="leading-snug text-gray-500">Some features we find in Topologies
                            </p>
                            <div className="h-6"></div>
                        </div>
                    </div>
                    <div className="lg:col-span-2 grid lg:grid-cols-2 gap-4">
                        {heronTopologyDetails.map((data) => (
                            <LandingPanel key={data.id}  title={data.title} imageSource={data.image} to={"/distributed-computing/heron/topologies/" + data.slug} strapLine={data.content} enabled={data.enabled}  />
                        ))}
                    </div>
                </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 via-orange-50 to-orange-50 dark:from-gray-950 dark:to-gray-900 py-16 md:py-16">
                <div className="w-full px-4 grid lg:grid-cols-3 gap-x-6 gap-y-12 lg:gap-y-0">
                    <div className="lg:pr-12 lg:place-self-top">
                        <div className="flex flex-col ">
                            <svg className="text-green-400 text-2xl" width="1.5em" height="1.5em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 13V21L5 16V8L12 3L19 8L12 13Z" fill="currentColor" opacity="0.5"/>
                                <path d="M12 21V13M12 21L5.83752 16.5982C5.42695 16.305 5.22166 16.1583 5.11083 15.943C5 15.7276 5 15.4753 5 14.9708V8M12 21L18.1625 16.5982C18.573 16.305 18.7783 16.1583 18.8892 15.943C19 15.7276 19 15.4753 19 14.9708V8M12 13L5 8M12 13L19 8M5 8L10.8375 3.83034C11.3989 3.42938 11.6795 3.2289 12 3.2289C12.3205 3.2289 12.6011 3.42938 13.1625 3.83034L19 8" stroke="#222222"  strokeWidth="1.2" opacity="0.5"/>
                            </svg>
                            <div className="h-2"></div>
                            <div className="flex  rounded-lg">
                                <div>
                                    <h2 className="text-2xl font-semibold
											leading-tight text-gray-800 false">Simple Examples
                                    </h2></div>
                            </div>
                            <div className="h-6"></div>
                            <p className="leading-snug text-gray-500">Some simple examples with Apache Heron
                            </p>
                            <div className="h-6"></div>
                        </div>
                    </div>
                    <div className="lg:col-span-2 grid lg:grid-cols-2 gap-4">
                        {heronSimpleExamples.map((data) => (
                            <LandingPanel key={data.id}  title={data.title} imageSource={data.image} to={"/distributed-computing/heron/examples/" + data.slug} strapLine={data.content} enabled={data.enabled}  />
                        ))}
                    </div>
                </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 via-orange-50 to-orange-50 dark:from-gray-950 dark:to-gray-900 py-16 md:py-16">
                <div className="w-full px-4 grid lg:grid-cols-3 gap-x-6 gap-y-12 lg:gap-y-0">
                    <div className="lg:pr-12 lg:place-self-top">
                        <div className="flex flex-col ">
                            <svg className="text-fuchsia-800 text-2xl" width="1.5em" height="1.5em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 13V21L5 16V8L12 3L19 8L12 13Z" fill="currentColor" opacity="0.5"/>
                                <path d="M12 21V13M12 21L5.83752 16.5982C5.42695 16.305 5.22166 16.1583 5.11083 15.943C5 15.7276 5 15.4753 5 14.9708V8M12 21L18.1625 16.5982C18.573 16.305 18.7783 16.1583 18.8892 15.943C19 15.7276 19 15.4753 19 14.9708V8M12 13L5 8M12 13L19 8M5 8L10.8375 3.83034C11.3989 3.42938 11.6795 3.2289 12 3.2289C12.3205 3.2289 12.6011 3.42938 13.1625 3.83034L19 8" stroke="#222222"  strokeWidth="1.2" opacity="0.5"/>
                            </svg>
                            <div className="h-2"></div>
                            <div className="flex  rounded-lg">
                                <div>
                                    <h2 className="text-2xl font-semibold
											leading-tight text-gray-800 false">Advanced Examples
                                    </h2></div>
                            </div>
                            <div className="h-6"></div>
                            <p className="leading-snug text-gray-500">Some more in depth examples
                            </p>
                            <div className="h-6"></div>
                        </div>
                    </div>
                    <div className="lg:col-span-2 grid lg:grid-cols-2 gap-4">
                        {heronAdvancedExamples.map((data) => (
                            <LandingPanel key={data.id}  title={data.title} imageSource={data.image} to={"/distributed-computing/heron/examples/" + data.slug} strapLine={data.content} enabled={data.enabled}  />
                        ))}
                    </div>
                </div>
            </div>
                </section>
            </div>

        </React.Fragment>
    )
};

export default React.memo(HeronLanding);
